import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';

export type DocumentKind = 'uploaded' | 'generated';

export interface TagCreate {
  color?: string | null;
  name: string;
}

export interface TagDto {
  color: string | null;
  created_at: string;
  id: string;
  name: string;
  organization_id: string;
  updated_at: string;
}

export interface TagUpdate {
  color?: string | null;
  name?: string;
}

export function useCreateTag() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const qc = useQueryClient();
  return useMutation({
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags', orgId] });
    },
    mutationFn: async (body: TagCreate) => {
      const { data } = await clientApi.post<TagDto>(tagsBase(orgId!), body);
      return data;
    },
  });
}

export function useDeleteTag() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tagId: string) => {
      await clientApi.delete(`${tagsBase(orgId!)}/${tagId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags', orgId] });
      qc.invalidateQueries({ queryKey: ['document-tags', orgId] });
    },
  });
}

// ─── Tag CRUD ───────────────────────────────────────────────────

export function useDocumentTags(kind: DocumentKind, documentId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  return useQuery({
    enabled: !!orgId && !!documentId,
    queryKey: ['document-tags', orgId, kind, documentId],
    queryFn: async () => {
      const { data } = await clientApi.get<TagDto[]>(docTagsBase(orgId!, kind, documentId!));
      return data;
    },
  });
}

export function useSetDocumentTags(kind: DocumentKind, documentId: string) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const qc = useQueryClient();
  return useMutation({
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['document-tags', orgId, kind, documentId] });
    },
    mutationFn: async (tagIds: string[]) => {
      const { data } = await clientApi.put<TagDto[]>(docTagsBase(orgId!, kind, documentId), {
        tag_ids: tagIds,
      });
      return data;
    },
  });
}

export function useTags() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  return useQuery({
    enabled: !!orgId,
    queryKey: ['tags', orgId],
    queryFn: async () => {
      const { data } = await clientApi.get<TagDto[]>(tagsBase(orgId!));
      return data;
    },
  });
}

export function useUpdateTag() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const qc = useQueryClient();
  return useMutation({
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags', orgId] });
      qc.invalidateQueries({ queryKey: ['document-tags', orgId] });
    },
    mutationFn: async ({ body, tagId }: { tagId: string; body: TagUpdate }) => {
      const { data } = await clientApi.patch<TagDto>(`${tagsBase(orgId!)}/${tagId}`, body);
      return data;
    },
  });
}

// ─── Document <-> Tag assignment ─────────────────────────────────

function docTagsBase(orgId: string, kind: DocumentKind, docId: string) {
  return `/api/v1/organizations/${orgId}/document-tags/${kind}/${docId}`;
}

function tagsBase(orgId: string) {
  return `/api/v1/organizations/${orgId}/tags`;
}
